import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Music, Moon, Palette, Heart, Play, Mic, Sparkles, Download } from "lucide-react";
import { HiddenStar } from "../components/HiddenStar";
import { OfferBanner } from "../components/OfferBanner";
import { api, localDate, thumb, fileUrl, LOGO_URL } from "../lib/api";
import { Layout, SubscribeButton } from "../components/Layout";
import { BiniBubble } from "../components/Bini";
import { LangBadge } from "../components/Flags";
import { t } from "../i18n";

const NAV = [
  { to: "/comptines", label: "nav.songs", icon: Music, color: "#38B6FF", id: "nav-comptines-link" },
  { to: "/mode-dodo", label: "nav.dodo", icon: Moon, color: "#8A3FE0", id: "nav-dodo-link" },
  { to: "/coloriages", label: "nav.coloring", icon: Palette, color: "#4CC417", id: "nav-coloriages-link" },
  { to: "/espace-parents", label: "nav.parents", icon: Heart, color: "#FF5FA8", id: "nav-parents-link" },
];

const DailyCard = ({ song }) => (
  <div data-testid="rhyme-of-the-day-card" className="cloud-card puffs p-4 sm:p-6 mt-8 anim-pop" style={{ animationDelay: "120ms" }}>
    <Link to={`/comptines/${song.slug}`} className="block relative aspect-video overflow-hidden rounded-[28px] border-4 border-white shadow-lg">
      <img src={thumb(song.youtube_id)} alt="" className="h-full w-full object-cover" />
      <span className="absolute left-3 top-3"><LangBadge lang={song.language} testId="daily-lang-badge" /></span>
    </Link>
    <h2 data-testid="daily-song-title" className="mt-4 text-2xl sm:text-3xl font-extrabold leading-tight">{song.title}</h2>
    <div className="mt-4 flex flex-wrap gap-3">
      <Link to={`/comptines/${song.slug}`} data-testid="play-rhyme-btn" className="btn-3d is-ink flex-1 sm:flex-none" style={{ "--c": "#FFD21F" }}>
        <Play className="w-6 h-6 fill-current" /> {t("home.play")}
      </Link>
      {song.lyrics?.trim() && (
        <Link to={`/comptines/${song.slug}/karaoke`} data-testid="karaoke-toggle-btn" className="btn-3d flex-1 sm:flex-none" style={{ "--c": "#FF8A1F" }}>
          <Mic className="w-6 h-6" strokeWidth={2.5} /> {t("home.karaoke")}
        </Link>
      )}
    </div>
  </div>
);

const DailyColoring = ({ c }) => (
  <div data-testid="coloring-of-the-day-card" className="cloud-card mt-6 flex items-center gap-4 p-4 anim-pop" style={{ animationDelay: "180ms" }}>
    <img src={fileUrl(`/files/colorings/${c.id}/preview`)} alt="" className="h-32 w-24 shrink-0 rounded-[18px] border-4 border-white bg-white object-contain shadow" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-bold uppercase tracking-wide text-[#4CC417]">{t("home.coloringToday")}</p>
      <p data-testid="daily-coloring-title" className="text-xl font-extrabold leading-tight">{c.title}</p>
      <a href={fileUrl(`/files/colorings/${c.id}/pdf`)} download data-testid="daily-coloring-download-btn"
        className="btn-3d mt-3 !min-h-[52px] !text-base !px-4" style={{ "--c": "#4CC417" }}>
        <Download className="w-5 h-5" strokeWidth={2.5} /> {t("coloring.download")}
      </a>
    </div>
  </div>
);

export default function Home() {
  const [daily, setDaily] = useState(null);

  useEffect(() => {
    api.get("/daily", { params: { date: localDate() } }).then((r) => setDaily(r.data)).catch(() => setDaily(null));
  }, []);

  return (
    <Layout>
      <OfferBanner />
      <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:items-start">
        <section>
          <div className="relative mx-auto lg:mx-0 w-64 sm:w-80">
            <img src={LOGO_URL} alt="MIZUBINI" data-testid="home-main-logo"
              className="w-full rounded-[40px] border-[6px] border-white shadow-2xl anim-pop" />
            <HiddenStar id="home-cloud" className="-left-10 bottom-4" />
          </div>
          <BiniBubble testId="bini-daily-bubble" className="mt-8">
            <span className="block">{t("home.hello")}</span>
            <span className="block text-base font-medium opacity-80">{t("home.today")}</span>
          </BiniBubble>
          {daily?.song && <DailyCard song={daily.song} />}
          {daily?.coloring && <DailyColoring c={daily.coloring} />}
        </section>
        <section className="relative lg:pt-6">
          <HiddenStar id="home-menu" className="-top-8 right-2 lg:-top-4" />
          <nav className="grid grid-cols-2 gap-4 sm:gap-5" aria-label="Menu">
            {NAV.map(({ to, label, icon: Icon, color, id }, i) => (
              <Link key={to} to={to} data-testid={id}
                className="btn-3d !rounded-[36px] !min-h-[150px] sm:!min-h-[180px] flex-col !text-xl sm:!text-2xl anim-pop"
                style={{ "--c": color, animationDelay: `${200 + i * 80}ms` }}>
                <Icon className="w-12 h-12 sm:w-14 sm:h-14" strokeWidth={2.4} />
                <span className="leading-tight text-center">{t(label)}</span>
              </Link>
            ))}
            <Link to="/ciel-de-bini" data-testid="nav-sky-link"
              className="btn-3d is-ink col-span-2 !rounded-[36px] !min-h-[96px] !text-xl sm:!text-2xl anim-pop"
              style={{ "--c": "#FFD21F", animationDelay: "520ms" }}>
              <Sparkles className="w-10 h-10" strokeWidth={2.4} /> {t("nav.sky")}
            </Link>
          </nav>
          <SubscribeButton className="mt-8 w-full" />
        </section>
      </div>
    </Layout>
  );
}
